import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/api';
import { format, differenceInHours, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { CheckCircle, Clock, ArrowLeft, User, AlertTriangle, CalendarDays, Lock, Bell } from 'lucide-react';
import { DayCycleGreeting } from '@/components/DayCycleGreeting';
import PatientChatWidget from '@/components/PatientChatWidget';
import BookingContactFields from '@/components/BookingContactFields';

import { PrivacyConsent } from '@/components/PrivacyConsent';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMAGE = "https://images.unsplash.com/photo-1666718621210-e662947b5dc3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBkYXJrJTIwd29vZCUyMG9mZmljZSUyMGludGVyaW9yfGVufDB8fHx8MTc3MzY5NDY5MXww&ixlib=rb-4.1.0&q=85";

export default function BookingPage() {
  const { linkId } = useParams();
  const urlAction = new URLSearchParams(window.location.search).get('action');
  const urlApptId = new URLSearchParams(window.location.search).get('appt');
  const [linkData, setLinkData] = useState(null);
  const [linkError, setLinkError] = useState(null);
  const [linkLoading, setLinkLoading] = useState(!!linkId);
  const [settings, setSettings] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [step, setStep] = useState(1);
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState('');
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', contactMethod: '', contactValue: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [createdApptId, setCreatedApptId] = useState('');
  const [reminderLink, setReminderLink] = useState('');
  const [rescheduleMode, setRescheduleMode] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleSlots, setRescheduleSlots] = useState([]);
  const [rescheduling, setRescheduling] = useState(false);
  const [isAnonymousLink, setIsAnonymousLink] = useState(false);
  const [anonConfirmed, setAnonConfirmed] = useState(false);

  // OTP verification state
  const [patientVerified, setPatientVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpInfo, setOtpInfo] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [otpEmail, setOtpEmail] = useState('');
  const [otpUsesStoredEmail, setOtpUsesStoredEmail] = useState(false);
  const [otpName, setOtpName] = useState('');
  const [otpPhone, setOtpPhone] = useState('');
  const [otpStep, setOtpStep] = useState('identify'); // 'identify' | 'send' | 'verify'
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [otpRequired, setOtpRequired] = useState(true);

  // Check existing patient session on load
  useEffect(() => {
    if (!linkId) { setCheckingSession(false); return; }
    api.get('/patient/session').then(({ data }) => {
      if (data.authenticated && data.link_id === linkId) {
        setPatientVerified(true);
        setOtpEmail(data.email || '');
        setOtpName(data.patient_name || '');
        setOtpPhone(data.patient_phone || '');
        setFormData((prev) => ({
          ...prev,
          name: data.patient_name || prev.name,
          email: data.email || prev.email,
        }));
      }
    }).catch(() => {});
    // Check if OTP is required for this specialist
    api.get(`/patient/otp-required?booking_link_id=${linkId}`).then(({ data }) => {
      setOtpRequired(data.required);
      if (!data.required) setPatientVerified(true);
    }).catch(() => {});
    // Check if link has patient info (non-anonymous)
    api.get(`/public/booking-link/${linkId}`).then(({ data }) => {
      if (data.has_patient_email) {
        setOtpStep('send'); // Has data, skip identify
        setOtpUsesStoredEmail(true);
        setOtpEmail(data.patient_email_masked || data.patient_email || '');
        setOtpName(data.patient_name || '');
        setOtpPhone(data.patient_phone || '');
      } else {
        // Anonymous and legacy phone-only links ask the patient for an email.
        setOtpStep('identify');
        setOtpName(data.patient_name || '');
        setOtpPhone(data.patient_phone || '');
      }
    }).catch(() => {});
    setCheckingSession(false);
  }, [linkId]);

  useEffect(() => {
    if (linkId) {
      fetchLinkData();
    } else {
      setLinkError('no_link');
      setLinkLoading(false);
    }
  }, [linkId]);

  const fetchLinkData = async () => {
    try {
      const { data } = await api.get(`/public/booking-link/${linkId}`);
      setLinkData(data);
      const anonymous = !data.is_personal && !data.used;
      setIsAnonymousLink(anonymous);
      if (!anonymous) {
        setFormData((prev) => ({
          ...prev,
          name: data.patient_name || '',
          email: data.patient_email || '',
        }));
      }
      fetchSettings(data.psychologist_id, data.format_id || '');
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) setLinkError('not_found');
      else if (status === 410) setLinkError('expired');
      else setLinkError('error');
    }
    setLinkLoading(false);
  };

  const fetchSettings = async (psychologistId, fixedFormatId = '') => {
    try {
      const pid = psychologistId || linkData?.psychologist_id || '';
      const [settingsRes, overridesRes] = await Promise.all([
        api.get(`/public/settings`, { params: { psychologist_id: pid } }),
        api.get(`/public/schedule-overrides`, { params: { psychologist_id: pid } }),
      ]);
      setSettings(settingsRes.data);
      setOverrides(overridesRes.data);
      if (fixedFormatId) {
        const fixedFormat = (settingsRes.data.meeting_formats || []).find((item) => item.id === fixedFormatId);
        if (!fixedFormat) {
          setLinkError('format_unavailable');
          return;
        }
        setSelectedFormat(fixedFormat);
        setStep(2);
      }
    } catch {
      toast.error('Не удалось загрузить данные');
    }
  };

  const fetchSlots = async (date, formatId) => {
    setSlotsLoading(true);
    try {
      const { data } = await api.get(`/public/available-slots`, {
        params: { date: format(date, 'yyyy-MM-dd'), format_id: formatId, psychologist_id: linkData?.psychologist_id || '' },
      });
      setAvailableSlots(data);
    } catch {
      setAvailableSlots([]);
    }
    setSlotsLoading(false);
  };

  const handleDateSelect = (date) => {
    if (!date) return;
    setSelectedDate(date);
    setSelectedTime('');
    if (selectedFormat) {
      fetchSlots(date, selectedFormat.id);
    }
    setStep(3);
    // Auto-scroll to time slots
    setTimeout(() => {
      const el = document.getElementById('time-slots-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
  };

  const handleFormatSelect = (fmt) => {
    setSelectedFormat(fmt);
    setSelectedDate(null);
    setSelectedTime('');
    setAvailableSlots([]);
    setStep(2);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setStep(4);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Заполните имя и email');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      toast.error('Проверьте формат email');
      return;
    }
    if (!formData.contactMethod) {
      toast.error('Выберите удобный способ связи');
      return;
    }
    if (formData.contactMethod !== 'email' && !formData.contactValue.trim()) {
      toast.error(formData.contactMethod === 'telegram' ? 'Укажите Telegram' : 'Укажите телефон');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/book`, {
        patient_name: formData.name,
        patient_phone: formData.contactMethod === 'phone' ? formData.contactValue : '',
        patient_email: formData.email,
        contact_method: formData.contactMethod,
        contact_value: formData.contactMethod === 'email' ? formData.email : formData.contactValue,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time: selectedTime,
        format_id: selectedFormat.id,
        notes: formData.notes,
        link_id: linkId || '',
        psychologist_id: linkData?.psychologist_id || '',
      });
      setSubmitted(true);
      // Fetch reminder link
      if (data?.id) {
        setCreatedApptId(data.id);
        try {
          const { data: rl } = await api.get(`/public/reminder-link/${data.id}`);
          setReminderLink(rl.link || '');
        } catch {}
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка при создании записи');
    }
    setSubmitting(false);
  };

  const fetchRescheduleSlots = async (date) => {
    if (!linkData?.appointment) return;
    setSlotsLoading(true);
    try {
      const { data } = await api.get(`/public/available-slots`, {
        params: { date: format(date, 'yyyy-MM-dd'), format_id: linkData.appointment.format_id, psychologist_id: linkData?.psychologist_id || '' },
      });
      setRescheduleSlots(data);
    } catch { setRescheduleSlots([]); }
    setSlotsLoading(false);
  };

  const handleRescheduleDate = (date) => {
    if (!date) return;
    setRescheduleDate(date);
    setRescheduleTime('');
    fetchRescheduleSlots(date);
  };

  const submitReschedule = async () => {
    if (!rescheduleDate || !rescheduleTime || !linkData?.appointment) return;
    setRescheduling(true);
    try {
      await api.put(`/public/appointments/${linkId}/reschedule`, {
        date: format(rescheduleDate, 'yyyy-MM-dd'),
        time: rescheduleTime,
        format_id: linkData.appointment.format_id,
      });
      toast.success('Запись перенесена');
      setRescheduleMode(false);
      fetchLinkData(); // refresh
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка переноса');
    }
    setRescheduling(false);
  };

  const getHoursUntilAppt = () => {
    if (!linkData?.appointment) return 0;
    const a = linkData.appointment;
    try {
      const apptDate = new Date(`${a.date}T${a.time}:00`);
      return differenceInHours(apptDate, new Date());
    } catch { return 0; }
  };

  const isWorkingDay = (date) => {
    if (!settings?.working_hours) return false;
    const dateStr = format(date, 'yyyy-MM-dd');
    const dateOverrides = overrides.filter((o) => o.date === dateStr);
    if (dateOverrides.some((o) => o.override_type === 'remove')) return false;
    if (dateOverrides.some((o) => o.override_type === 'add')) return true;
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[date.getDay()];
    return settings.working_hours[dayName]?.enabled === true;
  };

  const requestOtp = async () => {
    setOtpSending(true);
    setOtpError('');
    try {
      const payload = { booking_link_id: linkId };
      // Personal links are bound to the stored address server-side. Anonymous
      // links send the address entered by the visitor.
      if (!otpUsesStoredEmail && otpEmail) payload.patient_email = otpEmail;
      if (otpName) payload.patient_name = otpName;
      if (otpPhone) payload.patient_phone = otpPhone;
      const { data } = await api.post('/patient/request-otp', payload);
      setOtpInfo(data);
      setOtpSent(true);
    } catch (err) {
      setOtpError(err.response?.data?.detail || 'Ошибка отправки кода');
    }
    setOtpSending(false);
  };

  const verifyOtp = async () => {
    setOtpVerifying(true);
    setOtpError('');
    try {
      await api.post('/patient/verify-otp', { booking_link_id: linkId, code: otpCode });
      setPatientVerified(true);
      const { data: session } = await api.get('/patient/session');
      const verifiedEmail = session.email || (!otpUsesStoredEmail ? otpEmail : '');
      // Pre-fill formData with OTP data
      setFormData(prev => ({
        ...prev,
        name: session.patient_name || otpName || prev.name,
        email: verifiedEmail || prev.email,
      }));
      setOtpEmail(verifiedEmail || otpEmail);
      setOtpName(session.patient_name || otpName);
      setOtpPhone(session.patient_phone || otpPhone);
      // Also skip anonymous confirm if we have data
      if ((session.patient_name || otpName) && verifiedEmail) setAnonConfirmed(true);
      await fetchLinkData();
    } catch (err) {
      setOtpError(err.response?.data?.detail || 'Неверный код');
    }
    setOtpVerifying(false);
  };

  // OTP verification gate — show before any patient data
  if (linkId && !patientVerified && !checkingSession) {
    return (
      <div data-testid="otp-verification" className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--app-sidebar)' }}>
        <div className="absolute inset-0 bg-cover bg-center opacity-[0.06]" style={{ backgroundImage: `url(${HERO_IMAGE})` }} />
        <Card className="relative z-10 w-full max-w-sm bg-stone-900/90 border-stone-800 shadow-2xl shadow-black/60 backdrop-blur-sm">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-full bg-amber-900/20 flex items-center justify-center mx-auto mb-4">
                <Lock size={28} className="text-amber-500" />
              </div>
              <h2 className="font-heading text-xl text-stone-200 mb-2">Верификация</h2>
              <p className="text-stone-500 text-sm">Для защиты ваших данных необходимо подтвердить личность</p>
            </div>

            {/* Step 1: Identify (for anonymous links) */}
            {otpStep === 'identify' && (
              <div className="space-y-3">
                <p className="text-stone-400 text-xs text-center mb-2">Укажите имя или псевдоним и email для кода</p>
                <input value={otpName} onChange={e => setOtpName(e.target.value)}
                  placeholder="Имя или псевдоним" className="w-full px-3 py-2.5 rounded-lg bg-stone-950/50 border border-stone-800 text-stone-200 text-sm placeholder:text-stone-600 focus:border-amber-700 focus:outline-none" />
                <input value={otpEmail} onChange={e => setOtpEmail(e.target.value)} type="email"
                  placeholder="Email (для получения кода)" className="w-full px-3 py-2.5 rounded-lg bg-stone-950/50 border border-stone-800 text-stone-200 text-sm placeholder:text-stone-600 focus:border-amber-700 focus:outline-none" />
                <p className="text-stone-500 text-[11px] leading-relaxed">Email хранится в ProPraxis и не будет показан специалисту, если позже вы выберете Telegram или телефон для связи.</p>
                <PrivacyConsent checked={privacyConsent} onChange={setPrivacyConsent} />
                <Button onClick={() => { if (otpEmail) setOtpStep('send'); else setOtpError('Укажите email для получения кода'); }}
                  disabled={!otpName || !otpEmail || !privacyConsent}
                  className="w-full bg-amber-700 hover:bg-amber-600 text-white h-11">
                  Продолжить
                </Button>
                {otpError && <p className="text-red-400 text-xs text-center">{otpError}</p>}
              </div>
            )}

            {/* Step 2: Send OTP */}
            {otpStep === 'send' && !otpSent && (
              <div className="space-y-4">
                {otpName && <p className="text-stone-400 text-sm text-center">{otpName}</p>}
                <p className="text-stone-500 text-xs text-center">Код будет отправлен на {otpEmail || 'email из персональной ссылки'}</p>
                <Button data-testid="send-otp-btn" onClick={requestOtp} disabled={otpSending}
                  className="w-full bg-amber-700 hover:bg-amber-600 text-white h-11">
                  {otpSending ? 'Отправка...' : 'Отправить код подтверждения'}
                </Button>
                {otpError && <p className="text-red-400 text-xs text-center">{otpError}</p>}
              </div>
            )}

            {/* Step 3: Verify OTP */}
            {otpSent && (
              <div className="space-y-4">
                {otpInfo?.email_sent ? (
                  <div className="text-center space-y-1">
                    <p className="text-stone-400 text-xs">Код отправлен на {otpInfo?.masked_email || otpEmail}</p>
                    <p className="text-stone-600 text-[11px]">Не пришёл? Проверьте папку «Спам» или запросите повторно.</p>
                  </div>
                ) : otpInfo?.delivery_status === 'not_configured' ? (
                  <p className="text-amber-500 text-xs text-center">Email-сервис не настроен. Код можно узнать у администратора (см. логи сервера).</p>
                ) : (
                  <p className="text-amber-500 text-xs text-center">Не удалось отправить код на {otpInfo?.masked_email || otpEmail}. Проверьте адрес и запросите повторно.</p>
                )}
                <div className="flex justify-center">
                  <input data-testid="otp-input" type="text" inputMode="numeric" maxLength={4}
                    value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    className="w-40 text-center text-2xl tracking-[0.5em] font-mono py-3 rounded-lg bg-stone-950/50 border border-stone-800 text-stone-200 focus:border-amber-700 focus:outline-none"
                    placeholder="····" autoFocus
                    onKeyDown={e => { if (e.key === 'Enter' && otpCode.length === 4) verifyOtp(); }}
                  />
                </div>
                <Button data-testid="verify-otp-btn" onClick={verifyOtp} disabled={otpVerifying || otpCode.length !== 4}
                  className="w-full bg-amber-700 hover:bg-amber-600 text-white h-11">
                  {otpVerifying ? 'Проверка...' : 'Подтвердить'}
                </Button>
                {otpError && <p className="text-red-400 text-xs text-center">{otpError}</p>}
                <button onClick={() => { setOtpSent(false); setOtpCode(''); setOtpError(''); }}
                  className="w-full text-stone-600 text-xs hover:text-stone-400 transition-colors">
                  Отправить повторно
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (checkingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--app-sidebar)' }}>
        <p className="text-stone-500">Проверка...</p>
      </div>
    );
  }

  if (linkLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--app-sidebar)' }}>
        <p className="text-stone-500">Загрузка...</p>
        {linkId && <PatientChatWidget bookingLinkId={linkId} patientName="" patientPhone="" />}
      </div>
    );
  }

  if (linkError) {
    const messages = {
      no_link: { title: 'Нужна ссылка для записи', desc: 'Запросите персональную ссылку у вашего психолога.' },
      not_found: { title: 'Ссылка не найдена', desc: 'Проверьте правильность ссылки или запросите новую.' },
      expired: { title: 'Ссылка недействительна', desc: 'Эта ссылка уже была использована или срок её действия истёк.' },
      format_unavailable: { title: 'Формат больше недоступен', desc: 'Специалист изменил список форматов. Запросите новую ссылку для записи.' },
      error: { title: 'Ошибка', desc: 'Не удалось загрузить данные. Попробуйте позже.' },
    };
    const msg = messages[linkError] || messages.error;
    return (
      <div data-testid="link-error-page" className="min-h-screen flex items-center justify-center p-4 relative" style={{ background: 'var(--app-sidebar)' }}>
        <div className="absolute inset-0 bg-cover bg-center opacity-10" style={{ backgroundImage: `url(${HERO_IMAGE})` }} />
        <Card className="relative z-10 w-full max-w-md bg-stone-900/90 border-stone-800 shadow-2xl shadow-black/60 backdrop-blur-sm">
          <CardContent className="p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-amber-900/20 flex items-center justify-center mx-auto mb-6">
              <AlertTriangle size={32} className="text-amber-500" />
            </div>
            <h2 className="font-heading text-2xl text-stone-200 mb-3">{msg.title}</h2>
            <p className="text-stone-400 text-sm leading-relaxed">{msg.desc}</p>
          </CardContent>
        </Card>
        {linkId && linkError !== 'no_link' && <PatientChatWidget bookingLinkId={linkId} patientName="" patientPhone="" />}
      </div>
    );
  }

  // Link is used — show existing appointment with reschedule option
  if (linkData?.used && linkData?.appointment) {
    const appt = linkData.appointment;
    const hoursLeft = getHoursUntilAppt();
    const canReschedule = hoursLeft >= 24;
    let dateStr;
    try { dateStr = format(parseISO(appt.date), 'd MMMM yyyy', { locale: ru }); } catch { dateStr = appt.date; }

    return (
      <div data-testid="appointment-view" className="min-h-screen flex items-center justify-center p-4 relative" style={{ background: 'var(--app-sidebar)' }}>
        <div className="absolute inset-0 bg-cover bg-center opacity-10" style={{ backgroundImage: `url(${HERO_IMAGE})` }} />
        <div className="relative z-10 w-full max-w-md flex flex-col items-center">
        <Card className="w-full bg-stone-900/90 border-stone-800 shadow-2xl shadow-black/60 backdrop-blur-sm">
          <CardContent className="p-8 space-y-6">
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-amber-900/20 flex items-center justify-center mx-auto mb-4">
                <CalendarDays size={28} className="text-amber-500" />
              </div>
              <h2 className="font-heading text-2xl text-stone-200 mb-1">Ваша запись</h2>
              <p className="text-stone-500 text-sm">у {settings?.psychologist_name || 'психолога'}</p>
            </div>

            <div className="p-4 rounded-lg bg-stone-950/50 border border-stone-800/50 space-y-2">
              <p className="text-stone-200 font-medium">{appt.patient_name}</p>
              <p className="text-amber-500 text-sm">{dateStr}, {appt.time}</p>
              <p className="text-stone-400 text-sm">{appt.format_name} — {appt.duration} мин.</p>
              {appt.status === 'confirmed' && <span className="inline-block text-xs bg-green-900/30 text-green-400 border border-green-900/50 rounded px-2 py-0.5">Подтверждена</span>}
              {appt.status === 'pending' && <span className="inline-block text-xs bg-amber-900/30 text-amber-400 border border-amber-900/50 rounded px-2 py-0.5">Ожидает подтверждения</span>}
            </div>

            {/* Patient confirm/cancel buttons */}
            {appt.status === 'pending' && (
              <div className="flex gap-2">
                <Button className="flex-1 bg-green-700 hover:bg-green-600 text-white"
                  onClick={async () => {
                    try {
                      await api.put(`/public/appointments/${appt.id}/patient-confirm?action=confirm`);
                      toast.success('Запись подтверждена');
                      fetchLinkData();
                    } catch { toast.error('Ошибка'); }
                  }}>
                  Подтвердить
                </Button>
                <Button variant="outline" className="flex-1 border-red-900/50 text-red-400 hover:bg-red-900/20"
                  onClick={async () => {
                    try {
                      await api.put(`/public/appointments/${appt.id}/patient-confirm?action=cancel`);
                      toast.success('Запись отменена');
                      fetchLinkData();
                    } catch { toast.error('Ошибка'); }
                  }}>
                  Отменить
                </Button>
              </div>
            )}

            {!rescheduleMode ? (
              <div className="space-y-3">
                {canReschedule ? (
                  <Button data-testid="start-reschedule-btn" onClick={() => { setRescheduleMode(true); fetchSettings(); }} className="w-full bg-amber-700 hover:bg-amber-600 text-white">
                    <CalendarDays size={16} className="mr-2" /> Перенести запись
                  </Button>
                ) : (
                  <div className="p-3 rounded-lg bg-stone-800/50 border border-stone-700/50 flex items-center gap-3">
                    <Lock size={16} className="text-stone-500 shrink-0" />
                    <p className="text-stone-500 text-xs">Перенос невозможен менее чем за 24 часа до сеанса</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-stone-400 text-sm font-medium">Выберите новую дату:</p>
                <Calendar
                  data-testid="reschedule-public-calendar"
                  mode="single"
                  selected={rescheduleDate}
                  onSelect={handleRescheduleDate}
                  locale={ru}
                  disabled={(d) => d < new Date(new Date().setHours(0,0,0,0)) || !isWorkingDay(d)}
                  className="rounded-lg border border-stone-800 bg-stone-950/50"
                />
                {rescheduleDate && (
                  <div>
                    <p className="text-stone-400 text-sm font-medium mb-2">Выберите время:</p>
                    {slotsLoading ? <p className="text-stone-500 text-xs">Загрузка...</p> : rescheduleSlots.length === 0 ? (
                      <p className="text-stone-600 text-xs">Нет доступных слотов</p>
                    ) : (
                      <div className="grid grid-cols-3 gap-2">
                        {rescheduleSlots.map(t => (
                          <Button key={t} variant={rescheduleTime === t ? 'default' : 'outline'} size="sm"
                            onClick={() => setRescheduleTime(t)}
                            className={rescheduleTime === t ? 'bg-amber-700 text-white' : 'border-stone-700 text-stone-300 hover:bg-stone-800'}>
                            {t}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={() => setRescheduleMode(false)} className="flex-1 bg-stone-800 text-stone-300 hover:bg-stone-700">
                    Отмена
                  </Button>
                  <Button data-testid="confirm-reschedule-btn" onClick={submitReschedule} disabled={!rescheduleDate || !rescheduleTime || rescheduling}
                    className="flex-1 bg-amber-700 hover:bg-amber-600 text-white">
                    {rescheduling ? 'Перенос...' : 'Подтвердить'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        {/* Telegram notifications link */}
        {appt && !appt.client_telegram_chat_id && (
          <div className="mt-3 text-center">
            <button onClick={async () => {
              try {
                const { data } = await api.get(`/public/reminder-link/${appt.id}`);
                if (data.link) window.open(data.link, '_blank');
              } catch { toast.error('Бот не доступен'); }
            }}
              className="text-xs text-amber-500/70 hover:text-amber-400 underline underline-offset-2 transition-colors">
              Подключить уведомления в Telegram
            </button>
          </div>
        )}
        </div>
        {linkId && <PatientChatWidget bookingLinkId={linkId} patientName={linkData?.patient_name || ''} patientPhone={linkData?.patient_phone || ''} />}
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 relative" style={{ background: 'var(--app-sidebar)' }}>
        <div className="absolute inset-0 bg-cover bg-center opacity-10" style={{ backgroundImage: `url(${HERO_IMAGE})` }} />
        <Card data-testid="booking-success" className="relative z-10 w-full max-w-md bg-stone-900/90 border-stone-800 shadow-2xl shadow-black/60 backdrop-blur-sm">
          <CardContent className="p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-green-900/30 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={32} className="text-green-400" />
            </div>
            <h2 className="font-heading text-2xl text-stone-200 mb-3">Запись создана</h2>
            <p className="text-stone-400 text-sm leading-relaxed">
              Ваша запись ожидает подтверждения. Вы получите уведомление, когда психолог подтвердит время встречи.
            </p>
            <div className="mt-6 p-4 rounded-lg bg-stone-950/50 border border-stone-800">
              <p className="text-stone-300 text-sm font-medium">{selectedFormat?.name}</p>
              <p className="text-amber-500 text-sm mt-1">
                {selectedDate && format(selectedDate, 'd MMMM yyyy', { locale: ru })}, {selectedTime}
              </p>
            </div>
            {reminderLink && (
              <a href={reminderLink} target="_blank" rel="noopener noreferrer"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-colors"
                style={{ background: 'var(--app-accent-muted)', color: 'var(--app-accent)', border: '1px solid var(--app-accent)' }}>
                <Bell size={16} /> Получить напоминание в Telegram
              </a>
            )}
          </CardContent>
        </Card>
        {linkId && <PatientChatWidget bookingLinkId={linkId} patientName={formData.name || ''} patientPhone={formData.contactMethod === 'phone' ? formData.contactValue : ''} />}
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="min-h-screen p-4 md:p-8 relative" style={{ background: 'var(--app-sidebar)' }}>
      <div className="absolute inset-0 bg-cover bg-center opacity-[0.06]" style={{ backgroundImage: `url(${HERO_IMAGE})` }} />

      <div className="relative z-10 max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10 pt-8 animate-fade-in">
          <DayCycleGreeting name={linkData?.patient_name || null} />
          <p className="text-sm md:text-base mt-3" style={{ color: 'var(--app-text-muted)' }}>Выберите удобное время для встречи</p>
        </div>

        {/* Steps */}
        <div className="flex items-center justify-center gap-2 mb-8 animate-fade-in delay-1">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all duration-300 ${
                s === step ? 'bg-amber-700 text-white' :
                s < step ? 'text-amber-500' : ''
              }`} style={s > step ? { background: 'var(--app-card)', color: 'var(--app-text-faint)' } : s < step ? { background: 'var(--app-accent-muted)' } : {}}>
                {s}
              </div>
              {s < 4 && <div className="w-8 h-px" style={{ background: s < step ? 'var(--app-accent)' : 'var(--app-border)' }} />}
            </div>
          ))}
        </div>

        {/* Step 0: Identify yourself (anonymous links) */}
        {isAnonymousLink && !anonConfirmed && (
          <div className="mb-6 booking-step">
            <h3 className="text-stone-400 text-sm font-medium mb-3 flex items-center gap-2">
              <User size={14} /> Представьтесь
            </h3>
            <div className="space-y-3">
              <Input data-testid="anon-name" placeholder="Ваше имя" value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-stone-950/50 border-stone-800 text-stone-200 placeholder:text-stone-600" />
              <Input data-testid="anon-email" type="email" placeholder="Email" value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-stone-950/50 border-stone-800 text-stone-200 placeholder:text-stone-600" />
              <Button data-testid="anon-continue" size="sm" disabled={!formData.name.trim() || !formData.email.trim()}
                onClick={() => setAnonConfirmed(true)}
                className="w-full bg-amber-700 hover:bg-amber-600 text-white">
                Продолжить
              </Button>
            </div>
          </div>
        )}

        {/* Step 1: Format */}
        {((!isAnonymousLink) || anonConfirmed) && step >= 1 && (
          <div className="mb-6 booking-step">
            <h3 className="text-sm font-medium mb-3 flex items-center gap-2" style={{ color: 'var(--app-text-secondary)' }}>
              <Clock size={14} /> Формат встречи
            </h3>
            {linkData?.format_id ? (
              <div
                data-testid={`format-${selectedFormat?.id || linkData.format_id}`}
                className="p-4 rounded-xl border text-left"
                style={{ background: 'var(--app-accent-muted)', borderColor: 'var(--app-accent)' }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium text-sm" style={{ color: 'var(--app-accent)' }}>
                      {selectedFormat?.name || linkData.format_name || 'Формат сеанса'}
                    </p>
                    {selectedFormat && (
                      <p className="text-xs mt-1" style={{ color: 'var(--app-text-muted)' }}>{selectedFormat.duration} мин.</p>
                    )}
                  </div>
                  <span className="rounded-full px-2 py-1 text-[10px]" style={{ color: 'var(--app-accent)', background: 'var(--app-card)' }}>
                    Задан в ссылке
                  </span>
                </div>
                {selectedFormat?.description && <p className="text-xs mt-2" style={{ color: 'var(--app-text-faint)' }}>{selectedFormat.description}</p>}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(settings?.meeting_formats || []).map((fmt) => (
                  <button
                    key={fmt.id}
                    data-testid={`format-${fmt.id}`}
                    onClick={() => handleFormatSelect(fmt)}
                    className="p-4 rounded-xl border text-left transition-opacity duration-200 hover:opacity-80"
                    style={{
                      background: selectedFormat?.id === fmt.id ? 'var(--app-accent-muted)' : 'var(--app-card)',
                      borderColor: selectedFormat?.id === fmt.id ? 'var(--app-accent)' : 'var(--app-border)',
                    }}
                  >
                    <p className="font-medium text-sm" style={{ color: selectedFormat?.id === fmt.id ? 'var(--app-accent)' : 'var(--app-text)' }}>
                      {fmt.name}
                    </p>
                    <p className="text-xs mt-1" style={{ color: 'var(--app-text-muted)' }}>{fmt.duration} мин.</p>
                    {fmt.description && <p className="text-xs mt-1" style={{ color: 'var(--app-text-faint)' }}>{fmt.description}</p>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Date */}
        {step >= 2 && selectedFormat && (
          <div className="mb-6 booking-step">
            <h3 className="text-sm font-medium mb-3 flex items-center gap-2" style={{ color: 'var(--app-text-secondary)' }}>
              {!linkData?.format_id && (
                <button
                  type="button"
                  aria-label="Вернуться к выбору формата"
                  className="hover:opacity-70"
                  style={{ color: 'var(--app-accent)' }}
                  onClick={() => { setStep(1); setSelectedFormat(null); }}
                >
                  <ArrowLeft size={14} />
                </button>
              )}
              Выберите дату
            </h3>
            <Card className="inline-block" style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
              <CardContent className="p-4">
                <Calendar
                  data-testid="booking-calendar"
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  locale={ru}
                  disabled={(date) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return date < today || !isWorkingDay(date);
                  }}
                  style={{ color: 'var(--app-text)' }}
                  classNames={{
                    day_selected: "bg-amber-700 text-white hover:bg-amber-600 focus:bg-amber-700",
                    day_today: "text-amber-500 font-bold",
                  }}
                />
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 3: Time */}
        {step >= 3 && selectedDate && (
          <div id="time-slots-section" className="mb-6 booking-step">
            <h3 className="text-sm font-medium mb-3 flex items-center gap-2" style={{ color: 'var(--app-text-secondary)' }}>
              <span className="cursor-pointer hover:opacity-70" style={{ color: 'var(--app-accent)' }} onClick={() => setStep(2)}>
                <ArrowLeft size={14} />
              </span>
              Выберите время ({format(selectedDate, 'd MMMM', { locale: ru })})
            </h3>
            {slotsLoading ? (
              <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>Загрузка слотов...</p>
            ) : availableSlots.length === 0 ? (
              <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>Нет доступных слотов на этот день</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {availableSlots.map((time) => (
                  <button
                    key={time}
                    data-testid={`slot-${time}`}
                    onClick={() => handleTimeSelect(time)}
                    className={`py-2.5 px-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                      selectedTime === time ? 'bg-amber-700 text-white shadow-lg' : ''
                    }`}
                    style={selectedTime === time ? {} : {
                      background: 'var(--app-card)', border: '1px solid var(--app-border)', color: 'var(--app-text)',
                    }}
                  >
                    {time}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 4: Form */}
        {step >= 4 && selectedTime && (
          <div className="booking-step">
            <h3 className="text-sm font-medium mb-3 flex items-center gap-2" style={{ color: 'var(--app-text-secondary)' }}>
              <span className="cursor-pointer hover:opacity-70" style={{ color: 'var(--app-accent)' }} onClick={() => setStep(3)}>
                <ArrowLeft size={14} />
              </span>
              Ваши данные
            </h3>
            <Card style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
              <CardContent className="p-6">
                {/* Summary */}
                <div className="mb-6 p-3 rounded-lg" style={{ background: 'var(--app-card-deep)', border: '1px solid var(--app-border)' }}>
                  <p className="text-sm" style={{ color: 'var(--app-text)' }}>{selectedFormat?.name}</p>
                  <p className="text-sm font-medium" style={{ color: 'var(--app-accent)' }}>
                    {format(selectedDate, 'd MMMM yyyy', { locale: ru })}, {selectedTime}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <BookingContactFields
                    value={formData}
                    onChange={setFormData}
                    emailReadOnly={patientVerified && otpRequired}
                  />
                  <Button
                    data-testid="submit-booking-btn"
                    type="submit"
                    disabled={submitting || !formData.name.trim() || !formData.email.trim() || !formData.contactMethod || (formData.contactMethod !== 'email' && !formData.contactValue.trim())}
                    className="w-full bg-amber-700 hover:bg-amber-600 text-white h-11 font-medium transition-all active:scale-[0.98] mt-2"
                  >
                    {submitting ? 'Отправка...' : 'Записаться'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      {linkId && (
        <PatientChatWidget
          bookingLinkId={linkId}
          patientName={linkData?.patient_name || ''}
          patientPhone={linkData?.patient_phone || ''}
        />
      )}
    </div>
  );
}
